import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  deriveDefaultInterval,
  isWithinRange,
  recenterRangeAround,
} from "../utils/timeContextMath";

export const SPEEDS = [1, 2, 8];

// The shared time context every panel in the workspace reads from: canvas,
// scrubber, alert tab, vitals tab. ONE WRITER PER GESTURE (see the API
// contract) -- every mutation goes through setRange / setIntervalS /
// setSubjectId / seek / the replay controls below, never through ad-hoc
// setState calls in a consumer.
//
// Two playhead representations, on purpose:
//  - `coarsePlayheadMs` (React state) drives anything that re-renders on
//    every change: the current-time readout, data refetches.
//  - `finePlayheadRef` (a ref) is written every rAF tick during playback and
//    on every pixel of a scrubber drag. Consumers that need per-frame
//    smoothness (the scrubber handle, the canvas position) read the ref via
//    `subscribe`, which fires far more often than a re-render should.
// `coarsePlayheadMs` is flushed from the ref at most once a second while
// playing, and immediately on any discrete gesture (seek, drag release).
export const useTimeContext = (initialRange, initialSubjectId = null) => {
  const [range, setRangeState] = useState(initialRange);
  const [intervalS, setIntervalSState] = useState(() =>
    deriveDefaultInterval(initialRange.endMs - initialRange.startMs),
  );
  const [subjectId, setSubjectIdState] = useState(initialSubjectId);
  const [coarsePlayheadMs, setCoarsePlayheadMs] = useState(
    initialRange.startMs,
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeedState] = useState(1);

  const finePlayheadRef = useRef(initialRange.startMs);
  const subscribersRef = useRef(new Set());
  const lastTickRef = useRef(null);
  const lastFlushRef = useRef(0);

  const subscribe = useCallback((callback) => {
    subscribersRef.current.add(callback);
    return () => subscribersRef.current.delete(callback);
  }, []);

  const notify = useCallback(() => {
    subscribersRef.current.forEach((callback) =>
      callback(finePlayheadRef.current),
    );
  }, []);

  const clampToRange = useCallback(
    (ms, targetRange) =>
      Math.min(Math.max(ms, targetRange.startMs), targetRange.endMs),
    [],
  );

  // Writer: range picker / presets. Resets intervalS to the derived default
  // (the whole point of the reset is that a stale fine granularity over a
  // newly-widened range would blow the point budget), clamps the playhead
  // into the new range.
  const setRange = useCallback(
    (newRange) => {
      setRangeState(newRange);
      setIntervalSState(
        deriveDefaultInterval(newRange.endMs - newRange.startMs),
      );
      const clamped = clampToRange(finePlayheadRef.current, newRange);
      finePlayheadRef.current = clamped;
      setCoarsePlayheadMs(clamped);
      notify();
    },
    [clampToRange, notify],
  );

  // Writer: granularity Segmented control. Range is left untouched.
  const setIntervalS = useCallback((next) => {
    setIntervalSState(next);
  }, []);

  // Writer: canvas / personnel-panel focus (no seek implied).
  const setSubjectId = useCallback((id) => {
    setSubjectIdState(id);
  }, []);

  // Writer: the scrubber, while dragging. Fine-only -- no re-render, no
  // refetch -- until `commitPlayhead` fires on release.
  const setFinePlayhead = useCallback(
    (ms) => {
      finePlayheadRef.current = clampToRange(ms, range);
      notify();
    },
    [range, clampToRange, notify],
  );

  // Writer: the scrubber, on release -- flushes the fine value to state.
  const commitPlayhead = useCallback(() => {
    setCoarsePlayheadMs(finePlayheadRef.current);
  }, []);

  // Writer: the +-10s jump buttons. A discrete gesture like seek/commit --
  // writes the fine ref, flushes coarse state, and notifies immediately.
  const jumpBy = useCallback(
    (deltaMs) => {
      const next = clampToRange(finePlayheadRef.current + deltaMs, range);
      finePlayheadRef.current = next;
      setCoarsePlayheadMs(next);
      notify();
    },
    [range, clampToRange, notify],
  );

  // Writer: a record click (alert row / vitals chart point). Re-ranges FIRST
  // when the target falls outside the current range (recenter +-30min
  // through the same setRange path), then seeks, optionally re-focusing the
  // subject in the same gesture.
  const seek = useCallback(
    (targetMs, { focusSubjectId } = {}) => {
      if (!isWithinRange(targetMs, range)) {
        const recentered = recenterRangeAround(targetMs);
        setRange(recentered);
      }
      finePlayheadRef.current = targetMs;
      setCoarsePlayheadMs(targetMs);
      notify();
      if (focusSubjectId !== undefined) setSubjectIdState(focusSubjectId);
    },
    [range, setRange, notify],
  );

  const setSpeed = useCallback((next) => {
    setSpeedState(next);
  }, []);

  const play = useCallback(() => {
    // Restart from the beginning if playback already ran off the end.
    if (finePlayheadRef.current >= range.endMs) {
      finePlayheadRef.current = range.startMs;
      setCoarsePlayheadMs(range.startMs);
      notify();
    }
    setIsPlaying(true);
  }, [range, notify]);

  const pause = useCallback(() => setIsPlaying(false), []);

  // The replay clock: writes the fine ref every animation frame, flushes
  // coarse state at most once a second so the rest of the tree does not
  // re-render 60 times a second.
  //
  // STOPPED-FLAG PATTERN, deliberately not just `cancelAnimationFrame`: the
  // 1Hz flush below calls setCoarsePlayheadMs from INSIDE the rAF callback,
  // which in React 17 is unbatched and force-flushes pending passive
  // effects. If a pause click's cleanup is still pending, that flush runs it
  // mid-tick -- cleanup cancels the frame id already consumed by this very
  // callback, then tick schedules a NEW frame nobody's cleanup owns, and the
  // loop escapes this effect for good. `stopped` is a closure-local latch
  // checked before every reschedule, so once cleanup sets it, no frame this
  // effect instance ever spawned can schedule another.
  useEffect(() => {
    if (!isPlaying) {
      lastTickRef.current = null;
      return undefined;
    }
    let stopped = false;
    let rafId = null;
    const tick = (timestampMs) => {
      if (stopped) return;
      if (lastTickRef.current == null) lastTickRef.current = timestampMs;
      const deltaMs = timestampMs - lastTickRef.current;
      lastTickRef.current = timestampMs;

      const next = finePlayheadRef.current + deltaMs * speed;
      if (next >= range.endMs) {
        finePlayheadRef.current = range.endMs;
        notify();
        setCoarsePlayheadMs(range.endMs);
        setIsPlaying(false);
        return;
      }

      finePlayheadRef.current = next;
      notify();
      if (timestampMs - lastFlushRef.current >= 1000) {
        lastFlushRef.current = timestampMs;
        setCoarsePlayheadMs(next);
      }
      if (!stopped) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      stopped = true;
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [isPlaying, speed, range.endMs, notify]);

  return useMemo(
    () => ({
      range,
      intervalS,
      subjectId,
      coarsePlayheadMs,
      finePlayheadRef,
      subscribe,
      isPlaying,
      speed,
      setRange,
      setIntervalS,
      setSubjectId,
      setFinePlayhead,
      commitPlayhead,
      jumpBy,
      seek,
      play,
      pause,
      setSpeed,
    }),
    [
      range,
      intervalS,
      subjectId,
      coarsePlayheadMs,
      subscribe,
      isPlaying,
      speed,
      setRange,
      setIntervalS,
      setSubjectId,
      setFinePlayhead,
      commitPlayhead,
      jumpBy,
      seek,
      play,
      pause,
      setSpeed,
    ],
  );
};
